import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Heart, ArrowLeft, Loader2 } from 'lucide-react';

export default function RegisterPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { checkAuth } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const res = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password, name }),
      });

      const data = await res.json();

      if (res.ok) {
        await checkAuth();
        navigate('/dashboard');
      } else {
        const detail = data?.detail;
        if (typeof detail === 'string') {
          setError(detail);
        } else if (Array.isArray(detail)) {
          setError(detail.map(e => e.msg || JSON.stringify(e)).join(', '));
        } else {
          setError(data?.message || 'Registration failed. Please try again.');
        }
      }
    } catch (err) {
      setError(`Connection error: ${err.message}. Please check your internet connection.`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-4 py-12">
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/4 right-1/4 w-[400px] h-[400px] bg-purple-200/30 rounded-full blur-[100px]" />
        <div className="absolute bottom-1/4 left-1/4 w-[300px] h-[300px] bg-amber-200/30 rounded-full blur-[80px]" />
      </div>

      <div className="w-full max-w-md relative z-10">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-[#1a1a2e]/50 hover:text-[#1a1a2e] mb-8 transition-colors"
          data-testid="back-to-home-link"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
          <span className="text-sm">Back to home</span>
        </Link>

        <Card className="border-[#1a1a2e]/10 bg-white shadow-xl">
          <CardHeader className="text-center pb-6">
            <div className="flex items-center justify-center gap-2.5 mb-4">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#7c3aed] to-[#db2777] flex items-center justify-center shadow-lg shadow-purple-500/20">
                <Heart className="w-5 h-5 text-white" strokeWidth={2} fill="currentColor" />
              </div>
              <span className="text-2xl font-bold text-[#1a1a2e]">iExtol</span>
            </div>
            <CardTitle className="text-2xl font-bold text-[#1a1a2e]">Join iExtol</CardTitle>
            <CardDescription className="text-[#1a1a2e]/50">
              Start honouring the quiet heroes in your life
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm" data-testid="register-error">
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="name" className="text-[#1a1a2e]/70">Your name</Label>
                <Input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your full name"
                  required
                  className="border-[#1a1a2e]/10 focus:border-[#7c3aed] focus:ring-[#7c3aed]/20 rounded-xl"
                  data-testid="register-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-[#1a1a2e]/70">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  className="border-[#1a1a2e]/10 focus:border-[#7c3aed] focus:ring-[#7c3aed]/20 rounded-xl"
                  data-testid="register-email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-[#1a1a2e]/70">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 6 characters"
                  required
                  className="border-[#1a1a2e]/10 focus:border-[#7c3aed] focus:ring-[#7c3aed]/20 rounded-xl"
                  data-testid="register-password-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-[#1a1a2e]/70">Confirm password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Repeat your password"
                  required
                  className="border-[#1a1a2e]/10 focus:border-[#7c3aed] focus:ring-[#7c3aed]/20 rounded-xl"
                  data-testid="register-confirm-password-input"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-[#7c3aed] to-[#db2777] hover:from-[#6d28d9] hover:to-[#be185d] text-white rounded-full py-6 font-medium shadow-lg shadow-purple-500/20 transition-all duration-300"
                data-testid="register-submit-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating account...
                  </>
                ) : (
                  'Get started'
                )}
              </Button>

              <p className="text-center text-sm text-[#1a1a2e]/50">
                Already have an account?{' '}
                <Link to="/login" className="text-[#7c3aed] hover:text-[#6d28d9] font-medium transition-colors" data-testid="login-link">
                  Sign in
                </Link>
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
